import { Box, Button, Group, LoadingOverlay, Modal, Text } from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import {
  useDeleteAiBridge,
  useInvalidateAiBridgeList
} from '@/queries/aiBridgeQueries'
import { showToast } from '@/theme/notifications'
import { AiBridge } from '@/types/aiBridge'

type DeleteAiBridgeModalProps = {
  opened: boolean
  aiBridge?: Pick<AiBridge, 'id' | 'name'>
  onClose: () => void
  onDelete: () => void
}

export const DeleteAiBridgeModal = ({
  opened,
  aiBridge,
  onClose,
  onDelete
}: DeleteAiBridgeModalProps) => {
  const intl = useIntl()

  const { mutateAsync: deleteAiBridge, isPending } = useDeleteAiBridge()
  const { invalidateAiBridgeList } = useInvalidateAiBridgeList()

  const handleDeleteAiBridge = async () => {
    if (!aiBridge) {
      return
    }

    try {
      await deleteAiBridge(aiBridge.id)

      void invalidateAiBridgeList()
      showToast(
        intl.formatMessage({ id: 'aiBridge.delete.success' }),
        'success'
      )
      onDelete()
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'aiBridge.delete.error' })
        ),
        'error'
      )
    }
  }

  return (
    <Modal
      size="lg"
      title={<FormattedMessage id="aiBridge.delete" />}
      opened={opened}
      onClose={onClose}
    >
      <Box pos="relative">
        <LoadingOverlay visible={isPending} />

        {aiBridge && (
          <Text size="sm" mb="xl">
            <FormattedMessage
              id="aiBridge.delete.confirmation"
              values={{
                name: <span>{aiBridge.name}</span>
              }}
            />
          </Text>
        )}

        <Group justify="end">
          <Button miw={120} variant="default" onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>

          <Button
            miw={120}
            color="red"
            onClick={() => void handleDeleteAiBridge()}
          >
            <FormattedMessage id="delete" />
          </Button>
        </Group>
      </Box>
    </Modal>
  )
}
